const DEFAULT_STATUS = 'open';
const DEFAULT_ASSIGNEE_TYPE = 'all';
const DEFAULT_SORT_BY = 'last_activity_at_desc';
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;
const CHAT_LIST_ROUTE_LABEL_QUERY_KEYS = [
  'labels',
  'label_id',
  'label_source_id',
];

const hasValue = (value) =>
  value !== undefined && value !== null && value !== '';

const compactObject = (object) =>
  Object.keys(object).reduce((acc, key) => {
    if (object[key] !== undefined) {
      acc[key] = object[key];
    }
    return acc;
  }, {});

const sortObjectKeys = (object) =>
  Object.keys(object)
    .sort()
    .reduce((acc, key) => {
      acc[key] = object[key];
      return acc;
    }, {});

const normalizeLabelsForPayload = (labels) => {
  if (!hasValue(labels)) {
    return undefined;
  }
  return Array.isArray(labels) ? labels : [labels];
};

const normalizeLabelsForCompare = (labels) => {
  if (!hasValue(labels)) {
    return null;
  }
  const values = Array.isArray(labels) ? labels : [labels];
  const normalized = values
    .filter(hasValue)
    .map((value) => String(value))
    .sort();
  return normalized.length ? normalized.join(',') : null;
};

const normalizeNullableString = (value) =>
  hasValue(value) ? String(value) : null;

const normalizeNumber = (value, fallback) =>
  hasValue(value) ? Number(value) : Number(fallback);

const normalizeBoolean = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const getRouteQueryString = (route, key, fallback = '') =>
  (route?.query?.[key] || fallback).toString();

const getRouteSearchQuery = (route = {}) =>
  getRouteQueryString(route, 'q').trim();

const getRouteQueryValue = (route, key) => route?.query?.[key] || null;

const normalizeFilterValueForLooseCompare = (value) =>
  Array.isArray(value) ? [...value].sort().join(',') : value;

const areConversationFiltersEquivalent = (left, right) => {
  if (!left || !right) {
    return left === right;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (JSON.stringify(leftKeys) !== JSON.stringify(rightKeys)) {
    return false;
  }

  return leftKeys.every((key) => {
    const leftValue = normalizeFilterValueForLooseCompare(left[key]);
    const rightValue = normalizeFilterValueForLooseCompare(right[key]);
    return String(leftValue) === String(rightValue);
  });
};

const shouldSyncConversationInbox = ({ activeInbox, conversationInbox } = {}) =>
  String(activeInbox) !== String(conversationInbox);

const isDashboardConversationInboxChange = ({ newInbox, oldInbox } = {}) =>
  (newInbox === 0 || newInbox === '0') && oldInbox !== 0 && oldInbox !== '0';

const hasActiveConversationLabelFilters = ({
  activeInboxFilter,
  chatListFilters,
} = {}) =>
  Boolean(chatListFilters?.labels || activeInboxFilter?.filterType === 'label');

const shouldPreserveConversationInboxFilters = ({
  hasActiveFilters = false,
  newInbox,
  oldInbox,
  routePath = '',
} = {}) =>
  Boolean(hasActiveFilters) &&
  (String(oldInbox) === String(newInbox) ||
    routePath.includes('/conversations/'));

const shouldSkipConversationInboxFetch = ({
  chatListCount = 0,
  currentFilters = {},
  newInbox,
} = {}) =>
  newInbox !== 0 &&
  chatListCount > 0 &&
  String(currentFilters.inboxId) === String(newInbox);

const resolveConversationInboxChange = ({
  activeInbox,
  activeInboxFilter,
  chatListCount = 0,
  chatListFilters = {},
  newInbox,
  oldInbox,
  routePath = '',
  routeQuery = {},
} = {}) => {
  if (newInbox === oldInbox) {
    return {
      action: 'none',
      shouldClearConversationFilters: false,
      shouldForceUpdate: false,
      shouldResetFetch: false,
      shouldSyncActiveInbox: false,
    };
  }

  const shouldSyncActiveInbox = shouldSyncConversationInbox({
    activeInbox,
    conversationInbox: newInbox,
  });

  if (routeQuery?.q) {
    return {
      action: 'search-route',
      shouldClearConversationFilters: false,
      shouldForceUpdate: false,
      shouldResetFetch: false,
      shouldSyncActiveInbox,
    };
  }

  const shouldClearConversationFilters = isDashboardConversationInboxChange({
    newInbox,
    oldInbox,
  });
  const hasActiveFilters = hasActiveConversationLabelFilters({
    activeInboxFilter,
    chatListFilters,
  });

  if (
    shouldPreserveConversationInboxFilters({
      hasActiveFilters,
      newInbox,
      oldInbox,
      routePath,
    })
  ) {
    return {
      action: 'preserve-filters',
      shouldClearConversationFilters,
      shouldForceUpdate: false,
      shouldResetFetch: false,
      shouldSyncActiveInbox,
    };
  }

  if (
    shouldSkipConversationInboxFetch({
      chatListCount,
      currentFilters: chatListFilters,
      newInbox,
    })
  ) {
    return {
      action: 'force-update',
      shouldClearConversationFilters,
      shouldForceUpdate: true,
      shouldResetFetch: false,
      shouldSyncActiveInbox,
    };
  }

  return {
    action: 'reset-fetch',
    shouldClearConversationFilters,
    shouldForceUpdate: false,
    shouldResetFetch: true,
    shouldSyncActiveInbox,
  };
};

const resolveEffectiveConversationInboxId = ({
  activeInbox,
  conversationInbox,
} = {}) => {
  let effectiveInboxId = conversationInbox;
  if (
    !effectiveInboxId ||
    effectiveInboxId === 0 ||
    Number(effectiveInboxId) === 0
  ) {
    effectiveInboxId = activeInbox || null;
  }

  return effectiveInboxId || null;
};

const buildChatListFilters = ({
  activeAssigneeTab,
  activeStatus,
  activeSortBy,
  baseFilters = {},
  conversationType,
  currentPage,
  filterContactId,
  includeLabeledConversations = false,
  itemsPerPage,
  label,
  priorityFilterEnabled = false,
  routeQuery = {},
  selectedAssigneeIdFilter,
} = {}) => {
  const labelsToUse = label || routeQuery.labels;
  const filters = {
    ...baseFilters,
    assigneeType:
      activeAssigneeTab || baseFilters.assigneeType || DEFAULT_ASSIGNEE_TYPE,
    status: activeStatus || baseFilters.status || DEFAULT_STATUS,
    sortBy: activeSortBy || baseFilters.sortBy || DEFAULT_SORT_BY,
    page: currentPage || baseFilters.page || DEFAULT_PAGE,
    per_page: itemsPerPage || baseFilters.per_page || DEFAULT_PER_PAGE,
    labels: normalizeLabelsForPayload(labelsToUse),
    label_id: hasValue(routeQuery.label_id) ? routeQuery.label_id : undefined,
    label_source_id: hasValue(routeQuery.label_source_id)
      ? routeQuery.label_source_id
      : undefined,
    ai_replied: hasValue(routeQuery.ai_replied)
      ? routeQuery.ai_replied
      : undefined,
    conversationType: hasValue(conversationType) ? conversationType : undefined,
    include_labeled: includeLabeledConversations,
    has_priority: Boolean(priorityFilterEnabled),
  };

  if (hasValue(selectedAssigneeIdFilter)) {
    filters.assignee_id = selectedAssigneeIdFilter;
  } else {
    delete filters.assignee_id;
  }

  if (hasValue(filterContactId)) {
    filters.contact_id = filterContactId;
  } else {
    delete filters.contact_id;
  }

  return compactObject(filters);
};

const buildPreservedLabelConversationFilters = ({
  activeAssigneeTab,
  activeSortBy,
  activeStatus,
  conversationType,
  currentPage,
  filterContactId,
  includeLabeledConversations = false,
  itemsPerPage,
  preserveLabel,
  selectedAssigneeIdFilter,
} = {}) => {
  const filters = {
    assigneeType: activeAssigneeTab,
    status: activeStatus,
    sortBy: activeSortBy,
    page: currentPage || DEFAULT_PAGE,
    per_page: itemsPerPage,
    labels: [preserveLabel],
    conversationType: conversationType || undefined,
    include_labeled: includeLabeledConversations,
  };

  if (selectedAssigneeIdFilter !== null) {
    filters.assignee_id = selectedAssigneeIdFilter;
  }

  if (
    filterContactId !== null &&
    filterContactId !== undefined &&
    filterContactId
  ) {
    filters.contact_id = filterContactId;
  }

  return filters;
};

const buildConversationFetchFilters = ({
  activeInbox,
  conversationInbox,
  filterContactId,
  includeLabeledConversations = false,
  itemsPerPage,
  page,
  priorityFilterEnabled = false,
  routeQuery = {},
  selectedAssigneeIdFilter,
  storeFilters = {},
} = {}) => ({
  page: page || DEFAULT_PAGE,
  status: storeFilters.status || DEFAULT_STATUS,
  assigneeType: storeFilters.assigneeType || DEFAULT_ASSIGNEE_TYPE,
  sortBy: storeFilters.sortBy || DEFAULT_SORT_BY,
  per_page: storeFilters.per_page || itemsPerPage,
  labels:
    storeFilters.labels || (routeQuery.labels ? [routeQuery.labels] : null),
  label_id: storeFilters.label_id || routeQuery.label_id || null,
  label_source_id:
    storeFilters.label_source_id || routeQuery.label_source_id || null,
  conversationType: storeFilters.conversationType || null,
  assignee_id: selectedAssigneeIdFilter || null,
  contact_id: filterContactId || null,
  include_labeled: includeLabeledConversations || false,
  ai_replied: storeFilters.ai_replied || routeQuery.ai_replied || null,
  has_priority: priorityFilterEnabled || false,
  inboxId: resolveEffectiveConversationInboxId({
    activeInbox,
    conversationInbox,
  }),
});

const normalizeConversationFiltersForCompare = (filters = {}) =>
  sortObjectKeys({
    ai_replied: normalizeNullableString(filters.ai_replied),
    assignee_id: normalizeNullableString(filters.assignee_id),
    assigneeType: filters.assigneeType || DEFAULT_ASSIGNEE_TYPE,
    contact_id: normalizeNullableString(filters.contact_id),
    conversationType: normalizeNullableString(filters.conversationType),
    has_priority: normalizeBoolean(filters.has_priority),
    inboxId: hasValue(filters.inboxId) ? String(filters.inboxId) : 'all',
    include_labeled: normalizeBoolean(filters.include_labeled),
    label_id: normalizeNullableString(filters.label_id),
    label_source_id: normalizeNullableString(filters.label_source_id),
    labels: normalizeLabelsForCompare(filters.labels),
    page: normalizeNumber(filters.page, DEFAULT_PAGE),
    per_page: normalizeNumber(filters.per_page, DEFAULT_PER_PAGE),
    sortBy: filters.sortBy || DEFAULT_SORT_BY,
    status: filters.status || DEFAULT_STATUS,
  });

const shouldUpdateChatListFilters = ({
  existingFilters = {},
  normalizeFilters = normalizeConversationFiltersForCompare,
  targetFilters = {},
} = {}) => {
  const existingNorm = normalizeFilters({
    ...existingFilters,
    inboxId: targetFilters.inboxId,
    page: targetFilters.page,
  });
  const targetNorm = normalizeFilters(targetFilters);

  return JSON.stringify(existingNorm) !== JSON.stringify(targetNorm);
};

const buildConversationQueryKey = (filters) =>
  JSON.stringify(normalizeConversationFiltersForCompare(filters));

const resolveChatListRouteChange = ({
  activeSearchQuery = '',
  isLoadingLabel = false,
  isSearchModeActive = false,
  newRoute = {},
  oldRoute = {},
} = {}) => {
  const newSearchQuery = getRouteSearchQuery(newRoute);
  const oldSearchQuery = getRouteSearchQuery(oldRoute);
  const hasSearchQuery = Boolean(newSearchQuery);

  if (hasSearchQuery) {
    const shouldLoadSearch =
      newSearchQuery !== oldSearchQuery &&
      (!isSearchModeActive || activeSearchQuery !== newSearchQuery);

    return {
      action: shouldLoadSearch ? 'load-search-results' : 'ignore-active-search',
      hasSearchQuery,
      searchQuery: newSearchQuery,
      shouldClearSearch: false,
      shouldResetFetch: false,
      status: null,
    };
  }

  const shouldClearSearch = Boolean(oldSearchQuery);
  const newStatus = getRouteQueryString(newRoute, 'status', DEFAULT_STATUS);
  const oldStatus = getRouteQueryString(oldRoute, 'status', DEFAULT_STATUS);

  if (newStatus !== oldStatus) {
    return {
      action: 'status-change',
      hasSearchQuery: false,
      searchQuery: '',
      shouldClearSearch,
      shouldResetFetch: newStatus !== 'resolved',
      status: newStatus,
    };
  }

  const labelChanged = CHAT_LIST_ROUTE_LABEL_QUERY_KEYS.some(
    (key) =>
      getRouteQueryValue(newRoute, key) !== getRouteQueryValue(oldRoute, key)
  );

  if (labelChanged) {
    return {
      action: isLoadingLabel ? 'ignore-label-loading' : 'label-filter-change',
      hasSearchQuery: false,
      searchQuery: '',
      shouldClearSearch,
      shouldResetFetch: !isLoadingLabel,
      status: null,
    };
  }

  const aiRepliedChanged =
    getRouteQueryValue(newRoute, 'ai_replied') !==
    getRouteQueryValue(oldRoute, 'ai_replied');

  if (aiRepliedChanged) {
    return {
      action: 'ai-replied-filter-change',
      hasSearchQuery: false,
      searchQuery: '',
      shouldClearSearch,
      shouldResetFetch: true,
      status: null,
    };
  }

  return {
    action: shouldClearSearch ? 'clear-search' : 'none',
    hasSearchQuery: false,
    searchQuery: '',
    shouldClearSearch,
    shouldResetFetch: false,
    status: null,
  };
};

export {
  areConversationFiltersEquivalent,
  buildChatListFilters,
  buildConversationFetchFilters,
  buildConversationQueryKey,
  buildPreservedLabelConversationFilters,
  hasActiveConversationLabelFilters,
  isDashboardConversationInboxChange,
  normalizeConversationFiltersForCompare,
  resolveChatListRouteChange,
  resolveConversationInboxChange,
  resolveEffectiveConversationInboxId,
  shouldPreserveConversationInboxFilters,
  shouldSkipConversationInboxFetch,
  shouldSyncConversationInbox,
  shouldUpdateChatListFilters,
};
